import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, Download, ShieldCheck, ClipboardList, Users, CalendarDays, BriefcaseBusiness, MessageSquare, Bell, ScrollText, RefreshCw, Plus, Pencil, Trash2, X, Store, MailPlus, HandHeart, MessageCircle, Send } from "lucide-react";
import "./AdminConsole.css";

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });

const TABS = [
  ["complaints", "Complaints", ClipboardList],
  ["users", "Users", Users],
  ["services", "Services", Store],
  ["events", "Events", CalendarDays],
  ["jobs", "Jobs", BriefcaseBusiness],
  ["suggestions", "Suggestions", MessageSquare],
  ["campaign_volunteers", "Volunteers", HandHeart],
  ["notifications", "Notifications", Bell],
  ["notification_templates", "Templates", MailPlus],
  ["audit_logs", "Audit log", ScrollText],
];

const SCHEMAS = {
  services: {
    fields: [
      ["name", "text", "e.g. Vijayanagar Hospital"],
      ["category", "select:temples,hospitals,police-stations,libraries,schools", "Category"],
      ["address", "text", "Street, area or ward"],
      ["phone", "text", "Phone number"],
      ["hours", "text", "Opening hours"],
    ],
    label: "Public Service",
  },
  events: { fields: [["title", "text", "Event title"], ["date", "text", "e.g. 18 May 2026"], ["place", "text", "Venue / area"], ["description", "textarea", "Short description"], ["approved", "checkbox", "Approved for public listing"]], label: "Event" },
  jobs: {
    fields: [
      ["title", "text", "Job title"],
      ["org", "text", "Company / Organisation"],
      ["category", "text", "Job category"],
      ["employment_type", "select:Full-time,Part-time,Contract,Internship,Temporary", "Employment type"],
      ["location", "text", "Work location"],
      ["salary", "text", "Salary / salary range"],
      ["experience", "text", "Experience required"],
      ["qualification", "text", "Qualification"],
      ["openings", "text", "Number of openings"],
      ["deadline", "text", "Application deadline"],
      ["employer_name", "text", "Recruiter / contact person"],
      ["employer_phone", "text", "Employer phone"],
      ["employer_email", "text", "Employer email"],
      ["contact_preference", "select:Phone,WhatsApp,Email,Website", "Preferred contact"],
      ["apply_link", "text", "Application URL"],
      ["website", "text", "Company website"],
      ["status", "select:Pending Approval,Published,Approved,Rejected,Expired", "Publication status"],
      ["description", "textarea", "Complete job description"]
    ],
    label: "Job"
  },
  notification_templates: { fields: [["key", "text", "unique.template.key"], ["channel", "select:email,sms,whatsapp,in_app", "Channel"], ["subject", "text", "Subject line"], ["body", "textarea", "Message body"]], label: "Template" },
};

const STATUSES = ["Reported", "In progress", "Resolved", "Rejected"];
const SUGGESTION_STATUSES = ["Under review", "Acknowledged", "Actioned", "Closed"];
const ROLES = ["resident", "staff", "admin"];

export default function AdminConsole({ user, onBack }) {
  const [tab, setTab] = useState("complaints");
  const [items, setItems] = useState([]);
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState("");
  const [drawer, setDrawer] = useState(null); // {mode:'create'|'edit'|'status'|'respond'|'user', row?}
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setError("");
    try {
      const [ov, list] = await Promise.all([api.get("/admin/overview"), api.get(`/admin/${tab}`)]);
      setOverview(ov.data); setItems(list.data);
    } catch (e) { setError(e.response?.data?.detail || "Admin access required"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const exportCsv = () => {
    if (!items.length) return;
    const cols = Object.keys(items[0]);
    const csv = [cols.join(","), ...items.map(x => cols.map(c => JSON.stringify(x[c] ?? "")).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `vijayanagar-${tab}.csv`; a.click();
  };

  const closeDrawer = () => setDrawer(null);
  const submit = async (payload, method, url) => {
    setBusy(true);
    try { await api({ method, url, data: payload }); closeDrawer(); await load(); }
    catch (e) { setError(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const downloadComplaintLetter = async (row) => {
    if (!row?.tracking_id) return;

    try {
      setBusy(true);

      const response = await api.get(
        `/admin/complaints/${encodeURIComponent(row.tracking_id)}/letter.docx`,
        { responseType: "blob" }
      );

      const blob = new Blob(
        [response.data],
        {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        }
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `Complaint-${row.tracking_id}-Department-Letter.docx`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(
        e.response?.data?.detail ||
        "Unable to generate the department letter."
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete this ${tab.slice(0, -1)}?`)) return;
    setBusy(true);
    try { await api.delete(
        tab === "complaints"
          ? `/admin/complaints/${encodeURIComponent(row.tracking_id)}`
          : `/admin/${tab}/${row.id}`
      ); await load(); }
    catch (e) { setError(e.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };

  const canCreate = tab in SCHEMAS;
  const canEdit = (row) => tab in SCHEMAS && row?.id;

  return (
    <div className="admin-page">
      <header className="admin-head">
        <button onClick={onBack} data-testid="admin-back-button"><ArrowLeft size={17} /> Public view</button>
        <div><p className="kicker">SECURE CONSOLE</p><h1>Operations desk</h1></div>
        <span className="admin-identity" data-testid="admin-identity"><ShieldCheck size={16} />{user?.name || "Administrator"}</span>
      </header>
      <div className="admin-layout">
        <aside className="admin-nav">
          {TABS.map(([id, label, Icon]) => (
            <button key={id} className={tab === id ? "active" : ""} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}>
              <Icon size={17} />{label}<span>{overview?.[id] ?? ""}</span>
            </button>
          ))}
        </aside>
        <main className="admin-main">
          <div className="admin-toolbar">
            <div><p className="kicker">{tab.replaceAll("_", " ").toUpperCase()}</p><h2>Manage {TABS.find(x => x[0] === tab)?.[1]}</h2></div>
            <div className="toolbar-actions">
              {tab === "campaign_volunteers" && items.length > 0 && (
                <button onClick={() => setDrawer({ mode: "broadcast" })} data-testid="admin-broadcast-button" className="primary-tone"><MessageCircle size={16} /> WhatsApp broadcast</button>
              )}
              {canCreate && <button onClick={() => setDrawer({ mode: "create" })} data-testid="admin-create-button" className="primary-tone"><Plus size={16} /> New</button>}
              <button onClick={load} data-testid="admin-refresh-button"><RefreshCw size={16} /> Refresh</button>
              <button onClick={exportCsv} data-testid="admin-export-button"><Download size={16} /> Export CSV</button>
            </div>
          </div>

          {error && <p className="admin-error" data-testid="admin-error">{error}</p>}

          <AdminTable tab={tab} items={items} onEdit={(row) => setDrawer({ mode: tab === "complaints" ? "status" : tab === "suggestions" ? "respond" : tab === "users" ? "user" : tab === "campaign_volunteers" ? "volunteer" : "edit", row })} onDelete={remove} onDownloadLetter={downloadComplaintLetter} canEdit={canEdit} />
        </main>
      </div>

      {drawer && (
        <AdminDrawer
          tab={tab}
          state={drawer}
          busy={busy}
          items={items}
          onClose={closeDrawer}
          onSubmit={submit}
        />
      )}
    </div>
  );
}

function AdminTable({ tab, items, onEdit, onDelete, onDownloadLetter, canEdit }) {
  if (!items.length) return <div className="empty-admin" data-testid="admin-empty">No records in this queue yet.</div>;

  if (tab === "audit_logs") {
    return (
      <div className="admin-table" data-testid="admin-data-table">
        <div className="table-head audit"><span>Entity</span><span>Action</span><span>Actor</span><span>When</span></div>
        {items.map((r, i) => (
          <div className="table-row audit" key={r.id || i} data-testid={`audit-row-${i}`}>
            <div><b>{r.entity_type} · {r.entity_id?.slice(0, 12)}</b><small>{summarizeAudit(r)}</small></div>
            <span className="status-pill">{r.action}</span>
            <div><b>{r.actor_name}</b><small>{r.actor_email}</small></div>
            <time>{r.at ? new Date(r.at).toLocaleString() : "—"}</time>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="admin-table" data-testid="admin-data-table">
      <div className="table-head"><span>Record</span><span>Status / detail</span><span>Created</span><span>Actions</span></div>
      {items.map((row, i) => {
        const primary = row.tracking_id || row.name || row.title || row.email || row.key || row.message || "Record";
        const secondary = row.category || row.role || row.author || row.channel || row.type || row.location || row.ward || row.phone || "";
        const status = row.status || row.role || row.channel || "";
        const created = row.created_at || row.at;
        return (
          <div className="table-row" key={row.id || row.tracking_id || row.email || i} data-testid={`admin-row-${tab}-${i}`}>
            <div><b>{primary}</b><small>{secondary}</small></div>
            <span className={`status-pill ${String(status).toLowerCase().replaceAll(" ", "-")}`}>{status || "—"}</span>
            <time>{created ? new Date(created).toLocaleDateString() : "—"}</time>
            <div className="row-actions">
              {tab === "complaints" && (
  <button
    type="button"
    className="icon-button"
    title="Download department letter"
    onClick={() => onDownloadLetter(row)}
    disabled={!onDownloadLetter}
  >
    <Download size={14} />
  </button>
)}

{tab === "campaign_volunteers" && row.phone && (
                <a href={waLink(row.phone, `Namaste ${row.name}, thank you for joining SM Shailesh's Vijayanagar movement.`)} target="_blank" rel="noreferrer" className="wa-btn" data-testid={`admin-wa-${i}`} title="Open WhatsApp"><MessageCircle size={14} /></a>
              )}
              {(canEdit(row) || tab === "complaints" || tab === "suggestions" || tab === "users" || tab === "campaign_volunteers") && (
                <button onClick={() => onEdit(row)} data-testid={`admin-edit-${tab}-${i}`}><Pencil size={14} /></button>
              )}
              {(canEdit(row) || tab === "complaints") && (
                 <button onClick={() => onDelete(row)} data-testid={`admin-delete-${tab}-${i}`}><Trash2 size={14} /></button>
               )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function summarizeAudit(r) {
  const p = r.previous_value, n = r.new_value;
  if (!p && n) return "created";
  if (p && !n) return "deleted";
  if (p && n) {
    const keys = Object.keys(n).slice(0, 2);
    return keys.map(k => `${k}: ${JSON.stringify(p[k])} → ${JSON.stringify(n[k])}`).join(" · ");
  }
  return "";
}

function AdminDrawer({ tab, state, busy, onClose, onSubmit, items }) {
  const { mode, row } = state;

  if (mode === "status") return <StatusDrawer row={row} busy={busy} onClose={onClose} onSubmit={onSubmit} />;
  if (mode === "respond") return <RespondDrawer row={row} busy={busy} onClose={onClose} onSubmit={onSubmit} />;
  if (mode === "user") return <UserDrawer row={row} busy={busy} onClose={onClose} onSubmit={onSubmit} />;
  if (mode === "volunteer") return <VolunteerDrawer row={row} busy={busy} onClose={onClose} onSubmit={onSubmit} />;
  if (mode === "broadcast") return <BroadcastDrawer volunteers={items} onClose={onClose} />;

  return <EntityDrawer tab={tab} mode={mode} row={row} busy={busy} onClose={onClose} onSubmit={onSubmit} />;
}

function DrawerShell({ title, subtitle, children, onClose }) {
  return (
    <div className="drawer-backdrop" onMouseDown={onClose} data-testid="admin-drawer">
      <aside className="drawer" onMouseDown={e => e.stopPropagation()}>
        <button className="drawer-close" onClick={onClose} data-testid="admin-drawer-close"><X size={16} /></button>
        <p className="kicker">{subtitle}</p><h3>{title}</h3>
        {children}
      </aside>
    </div>
  );
}

function EntityDrawer({ tab, mode, row, busy, onClose, onSubmit }) {
  const schema = SCHEMAS[tab];
  const [form, setForm] = useState(() => {
    const base = {}; schema.fields.forEach(([k, type]) => {
      base[k] = row?.[k] ?? (type === "checkbox" ? false : "");
    }); return base;
  });
  const change = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = (e) => {
    e.preventDefault();
    const payload = { ...form };
    onSubmit(payload, mode === "create" ? "post" : "patch", mode === "create" ? `/admin/${tab}` : `/admin/${tab}/${row.id}`);
  };
  return (
    <DrawerShell subtitle={mode === "create" ? "NEW ENTRY" : "EDIT ENTRY"} title={`${mode === "create" ? "Create" : "Update"} ${schema.label}`} onClose={onClose}>
      <form onSubmit={save} className="drawer-form">
        {schema.fields.map(([key, type, placeholder]) => (
          <FormField key={key} name={key} type={type} placeholder={placeholder} value={form[key]} onChange={v => change(key, v)} />
        ))}
        <button className="primary-btn" disabled={busy} data-testid="admin-drawer-save">{busy ? "Saving..." : "Save"}</button>
      </form>
    </DrawerShell>
  );
}

function FormField({ name, type, placeholder, value, onChange }) {
  const label = name.replaceAll("_", " ");
  const testId = `admin-field-${name}`;
  if (type === "textarea") return <label className="field"><span>{label}</span><textarea data-testid={testId} placeholder={placeholder} value={value || ""} onChange={e => onChange(e.target.value)} rows={4} /></label>;
  if (type === "checkbox") return <label className="field inline"><input type="checkbox" data-testid={testId} checked={!!value} onChange={e => onChange(e.target.checked)} /> <span>{placeholder || label}</span></label>;
  if (type.startsWith("select:")) {
    const opts = type.slice(7).split(",");
    return <label className="field"><span>{label}</span><select data-testid={testId} value={value || opts[0]} onChange={e => onChange(e.target.value)}>{opts.map(o => <option key={o} value={o}>{o}</option>)}</select></label>;
  }
  return <label className="field"><span>{label}</span><input data-testid={testId} placeholder={placeholder} value={value || ""} onChange={e => onChange(e.target.value)} /></label>;
}

function StatusDrawer({ row, busy, onClose, onSubmit }) {
  const [status, setStatus] = useState(row.status || "Reported");
  const [note, setNote] = useState("");
  const save = (e) => { e.preventDefault(); onSubmit({ status, note }, "patch", `/admin/complaints/${row.tracking_id}`); };
  return (
    <DrawerShell subtitle="COMPLAINT UPDATE" title={`Update ${row.tracking_id}`} onClose={onClose}>
      <form onSubmit={save} className="drawer-form">
        <label className="field"><span>Current title</span><input value={row.title || ""} disabled /></label>
        <label className="field"><span>Status</span>
          <select value={status} onChange={e => setStatus(e.target.value)} data-testid="admin-complaint-status">
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </label>
        <label className="field"><span>Note to resident</span><textarea rows={4} value={note} onChange={e => setNote(e.target.value)} placeholder="What has been done or planned?" data-testid="admin-complaint-note" /></label>
        <button className="primary-btn" disabled={busy} data-testid="admin-drawer-save">{busy ? "Saving..." : "Post update"}</button>
      </form>
    </DrawerShell>
  );
}

function RespondDrawer({ row, busy, onClose, onSubmit }) {
  const [status, setStatus] = useState(row.status || "Under review");
  const [response, setResponse] = useState(row.response || "");
  const save = (e) => { e.preventDefault(); onSubmit({ status, response }, "patch", `/admin/suggestions/${row.id}`); };
  return (
    <DrawerShell subtitle="SUGGESTION" title={row.title || "Respond"} onClose={onClose}>
      <form onSubmit={save} className="drawer-form">
        <label className="field"><span>Suggestion</span><textarea value={row.description || ""} disabled rows={3} /></label>
        <label className="field"><span>Status</span>
          <select value={status} onChange={e => setStatus(e.target.value)} data-testid="admin-suggestion-status">
            {SUGGESTION_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </label>
        <label className="field"><span>Public response</span><textarea rows={4} value={response} onChange={e => setResponse(e.target.value)} data-testid="admin-suggestion-response" /></label>
        <button className="primary-btn" disabled={busy} data-testid="admin-drawer-save">{busy ? "Saving..." : "Save response"}</button>
      </form>
    </DrawerShell>
  );
}

function UserDrawer({ row, busy, onClose, onSubmit }) {
  const [role, setRole] = useState(row.role || "resident");
  const [active, setActive] = useState(row.active !== false);
  const save = (e) => { e.preventDefault(); onSubmit({ role, active }, "patch", `/admin/users/${row.id}`); };
  return (
    <DrawerShell subtitle="USER" title={row.name || row.email} onClose={onClose}>
      <form onSubmit={save} className="drawer-form">
        <label className="field"><span>Email</span><input value={row.email || ""} disabled /></label>
        <label className="field"><span>Role</span>
          <select value={role} onChange={e => setRole(e.target.value)} data-testid="admin-user-role">
            {ROLES.map(r => <option key={r}>{r}</option>)}
          </select>
        </label>
        <label className="field inline">
          <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} data-testid="admin-user-active" />
          <span>Account active</span>
        </label>
        <button className="primary-btn" disabled={busy} data-testid="admin-drawer-save">{busy ? "Saving..." : "Save"}</button>
      </form>
    </DrawerShell>
  );
}


const VOLUNTEER_STATUSES = ["New", "Contacted", "Onboarded", "Ward Lead", "Inactive"];

function waLink(phone, message) {
  const clean = String(phone).replace(/[^\d]/g, "");
  const digits = clean.length === 10 ? `91${clean}` : clean;
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

function VolunteerDrawer({ row, busy, onClose, onSubmit }) {
  const [status, setStatus] = useState(row.status || "New");
  const [note, setNote] = useState("");
  const save = (e) => { e.preventDefault(); onSubmit({ status, note }, "patch", `/admin/campaign_volunteers/${row.id}`); };
  const history = row.history || [];

  return (
    <DrawerShell subtitle="CAMPAIGN VOLUNTEER" title={row.name} onClose={onClose}>
      <div className="volunteer-summary">
        <div><span>Ward</span><b>{row.ward}</b></div>
        <div><span>Phone</span><b>{row.phone}</b></div>
        {row.email && <div><span>Email</span><b>{row.email}</b></div>}
        {row.message && <div className="full"><span>They said</span><b>{row.message}</b></div>}
      </div>
      <form onSubmit={save} className="drawer-form">
        <label className="field"><span>Set status</span>
          <select value={status} onChange={e => setStatus(e.target.value)} data-testid="admin-volunteer-status">
            {VOLUNTEER_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </label>
        <label className="field"><span>Internal note</span>
          <textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Called on 8/28, ready for door-to-door this weekend." data-testid="admin-volunteer-note" />
        </label>
        <a className="wa-inline" href={waLink(row.phone, `Namaste ${row.name}, thank you for joining SM Shailesh's Vijayanagar movement. Can we schedule a quick call this week?`)} target="_blank" rel="noreferrer" data-testid="admin-volunteer-wa">
          <MessageCircle size={14} /> Open WhatsApp for {row.name}
        </a>
        <button className="primary-btn" disabled={busy} data-testid="admin-drawer-save">{busy ? "Saving..." : "Save & log"}</button>
      </form>
      {history.length > 0 && (
        <section className="volunteer-history" data-testid="admin-volunteer-history">
          <p className="kicker">Status history</p>
          <ol>
            {history.map((h, i) => (
              <li key={i} data-testid={`volunteer-history-${i}`}>
                <b>{h.status}</b>
                <span>{h.note}</span>
                <time>{h.at ? new Date(h.at).toLocaleString() : ""} · {h.by || "system"}</time>
              </li>
            ))}
          </ol>
        </section>
      )}
    </DrawerShell>
  );
}

function BroadcastDrawer({ volunteers, onClose }) {
  const wards = Array.from(new Set(volunteers.map(v => v.ward))).sort();
  const [wardFilter, setWardFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [message, setMessage] = useState("Namaste! SM Shailesh is walking your ward this weekend. Reply YES to join us and receive the meeting point.");
  const filtered = volunteers.filter(v =>
    (wardFilter === "all" || v.ward === wardFilter) &&
    (statusFilter === "all" || v.status === statusFilter)
  );

  const copyPhones = () => {
    navigator.clipboard?.writeText(filtered.map(v => v.phone).join(", "));
  };
  const downloadCsv = () => {
    const rows = [["name","phone","ward","status"], ...filtered.map(v => [v.name, v.phone, v.ward, v.status || "New"])];
    const csv = rows.map(r => r.map(x => `"${String(x).replaceAll('"','""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "campaign-volunteers.csv"; a.click();
  };

  return (
    <DrawerShell subtitle="WHATSAPP BROADCAST" title="Reach your ward volunteers" onClose={onClose}>
      <div className="drawer-form broadcast-form">
        <div className="broadcast-filters">
          <label className="field"><span>Ward</span>
            <select value={wardFilter} onChange={e => setWardFilter(e.target.value)} data-testid="broadcast-ward-filter">
              <option value="all">All wards ({volunteers.length})</option>
              {wards.map(w => <option key={w} value={w}>{w}</option>)}
            </select>
          </label>
          <label className="field"><span>Status</span>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="broadcast-status-filter">
              <option value="all">All</option>
              {VOLUNTEER_STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
          </label>
        </div>
        <label className="field"><span>Message (each volunteer will get this pre-filled)</span>
          <textarea rows={4} value={message} onChange={e => setMessage(e.target.value)} data-testid="broadcast-message" />
        </label>
        <p className="broadcast-count" data-testid="broadcast-count">
          <b>{filtered.length}</b> volunteer{filtered.length === 1 ? "" : "s"} will be reachable — clicking each button opens WhatsApp with your message ready to send.
        </p>
        <div className="broadcast-utils">
          <button type="button" onClick={copyPhones} disabled={!filtered.length} data-testid="broadcast-copy">Copy phone list</button>
          <button type="button" onClick={downloadCsv} disabled={!filtered.length} data-testid="broadcast-csv">Download CSV</button>
        </div>
      </div>

      <div className="broadcast-list" data-testid="broadcast-recipients">
        {filtered.length === 0 && <p className="empty-admin small">No volunteers match this filter.</p>}
        {filtered.map((v, i) => (
          <a key={v.id || i}
            href={waLink(v.phone, message.replaceAll("{name}", v.name).replaceAll("{ward}", v.ward))}
            target="_blank" rel="noreferrer"
            className="broadcast-item"
            data-testid={`broadcast-item-${i}`}>
            <div>
              <b>{v.name}</b>
              <span>{v.ward} · {v.phone}</span>
            </div>
            <span className={`status-pill ${String(v.status || "New").toLowerCase().replaceAll(" ", "-")}`}>{v.status || "New"}</span>
            <Send size={14} />
          </a>
        ))}
      </div>
    </DrawerShell>
  );
}
